import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MESSAGE_SERVICE_TOKEN } from '@letly/services';
import { getPlatformChecks } from '../utils/platform-utils';
import { PwaInstallComponent } from '../../ui-common/pwa-install/pwa-install.component';


@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly doc = inject(DOCUMENT);
  private messageService = inject(MESSAGE_SERVICE_TOKEN);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  private readonly PROMPT_DURATION_MS = 1000 * 60 * 60 * 24;

  private get win(): Window | null {
    return this.doc.defaultView;
  }

  readonly isIos: boolean;
  readonly isAndroid: boolean;
  readonly isStandalone: boolean;

  constructor() {
    const { isIos, isAndroid, isStandalone } = getPlatformChecks(this.doc);
    this.isIos = isIos;
    this.isAndroid = isAndroid;
    this.isStandalone = isStandalone;

    if (this.win) {
      this.win.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        this.deferredPrompt = e as BeforeInstallPromptEvent;
      });
    }
  }

  promptIfEligible(): void {
    if (this.isStandalone || (!this.isIos && !this.isAndroid)) return;
    if (!this.isIos && !this.deferredPrompt) return;

    const lastShown = localStorage.getItem('pwa_prompt_date');
    const now = Date.now();

    if (lastShown) {
      const lastShownDate = new Date(lastShown).getTime();
      if (now - lastShownDate < this.PROMPT_DURATION_MS) return;
    }

    this.messageService.showDialogOrBottomSheet(PwaInstallComponent, {
      disableClose: false,
      bottomSheet: { panelClass: 'pwa-install-sheet' }
    }).subscribe(result => {
      localStorage.setItem('pwa_prompt_date', new Date(now).toISOString());
      if (result === 'install') this.triggerAndroidInstall();
    });
  }

  private async triggerAndroidInstall(): Promise<void> {
    if (!this.deferredPrompt) return;
    await this.deferredPrompt.prompt();
    this.deferredPrompt = null;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;

  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
